import { ProjectSection } from '../models/project/project-section.model';
import { OrganizationType } from '../models/staff/organization-type.enum';
import { Organization } from '../models/staff/organization.model';

export interface DevelopmentOrganizationScope {
  sectionId?: number;
  superviseSectionId?: number;
}

export interface DevelopmentOrganizationUserContext {
  organizationType?: OrganizationType;
  organizationId?: number;
  sectionId?: number;
}

export interface DevelopmentOrganizationFilterOptions {
  organizations: Organization[];
  sections: ProjectSection[];
  defaultDevelopmentId?: number;
  scope: DevelopmentOrganizationScope;
  user: DevelopmentOrganizationUserContext;
}

export class DevelopmentOrganizationUtil {
  static filter({
    organizations,
    sections,
    defaultDevelopmentId,
    scope,
    user,
  }: DevelopmentOrganizationFilterOptions): Organization[] {
    const developmentIds: number[] = [];
    const addDevelopmentId = (organizationId?: number) => {
      if (
        organizationId !== undefined &&
        !developmentIds.includes(organizationId)
      ) {
        developmentIds.push(organizationId);
      }
    };
    const addSectionDevelopmentOrganizations = (section?: ProjectSection) => {
      const sectionDevelopmentIds = this.getSectionDevelopmentIds(section);
      if (sectionDevelopmentIds.length === 0) {
        addDevelopmentId(defaultDevelopmentId);
        return;
      }
      for (const organizationId of sectionDevelopmentIds) {
        addDevelopmentId(organizationId);
      }
    };

    if (scope.sectionId) {
      addSectionDevelopmentOrganizations(
        sections.find((section) => section.id === scope.sectionId),
      );
    } else if (scope.superviseSectionId) {
      const superviseSection = sections.find(
        (section) => section.id === scope.superviseSectionId,
      );
      for (const sectionId of this.getConstructionSectionIds(
        superviseSection,
      )) {
        addSectionDevelopmentOrganizations(
          sections.find((section) => section.id === sectionId),
        );
      }
    } else if (user.organizationType === OrganizationType.DEVELOPMENT) {
      // 需求改了。如果是默认的建设单位用户，则需要拉取所有的建设单位组织
      if (user.organizationId === defaultDevelopmentId) {
        return organizations;
      } else {
        addDevelopmentId(user.organizationId);
        addDevelopmentId(defaultDevelopmentId);
      }
    } else {
      const userSection = sections.find(
        (section) => section.id === user.sectionId,
      );
      for (const sectionId of this.getConstructionSectionIds(userSection)) {
        addSectionDevelopmentOrganizations(
          sections.find((section) => section.id === sectionId),
        );
      }
    }

    if (developmentIds.length === 0) {
      addDevelopmentId(defaultDevelopmentId);
    }

    return organizations.filter((organization) =>
      developmentIds.includes(organization.id!),
    );
  }

  static collectByConstructionSectionIds(
    sections: ProjectSection[],
    constructionSectionIds: number[],
    defaultDevelopmentId?: number,
  ): number[] {
    const developmentIds: number[] = [];
    const addDevelopmentId = (organizationId?: number) => {
      if (
        organizationId !== undefined &&
        !developmentIds.includes(organizationId)
      ) {
        developmentIds.push(organizationId);
      }
    };

    for (const sectionId of constructionSectionIds) {
      const section = sections.find((item) => item.id === sectionId);
      const sectionDevelopmentIds = this.getSectionDevelopmentIds(section);
      if (sectionDevelopmentIds.length === 0) {
        addDevelopmentId(defaultDevelopmentId);
        continue;
      }
      for (const organizationId of sectionDevelopmentIds) {
        addDevelopmentId(organizationId);
      }
    }

    if (developmentIds.length === 0) {
      addDevelopmentId(defaultDevelopmentId);
    }
    return developmentIds;
  }

  private static getSectionDevelopmentIds(section?: ProjectSection): number[] {
    return (section?.projectSectionDlOrganizations ?? [])
      .filter((org) => org.isAgent && org.organizationId !== undefined)
      .map((org) => org.organizationId!);
  }

  private static getConstructionSectionIds(section?: ProjectSection): number[] {
    return (section?.projectSectionSections ?? [])
      .filter(
        (refSection) =>
          refSection.refSectionType === OrganizationType.CONSTRUCTION &&
          refSection.refSectionId !== undefined,
      )
      .map((refSection) => refSection.refSectionId!);
  }
}
